<script setup>
import { ref, computed } from "vue";
import Logo from "@/components/global/Logo";
import { useUserStore } from "@/stores";
import { email, helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

const userStore = useUserStore();

const form = ref({
  email: null,
  password: null,
});

const resetForm = ref({
  email: null,
});

const rules = {
  email: {
    required: helpers.withMessage("Email is required", required),
    email: helpers.withMessage("Invalid email format", email),
  },
  password: {
    required: helpers.withMessage("Password is required", required),
  },
};

const requestRules = {
  email: {
    required: helpers.withMessage("Email is required", required),
    email: helpers.withMessage("Invalid email format", email),
  },
};

const resetView = ref(false);

function handleForgotClick() {
  resetView.value = !resetView.value;
}

const v$ = useVuelidate(rules, form);
const v2$ = useVuelidate(requestRules, resetForm);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

const getResetErrorMessages = (field) =>
  computed(() =>
    v2$.value[field]?.$error
      ? v2$.value[field].$errors.map((e) => e.$message)
      : []
  );

const showPass = ref(false);

async function handleRequestClick() {
  const isValid = await v2$.value.$validate();
  if (isValid) {
    await userStore.requestPassword(resetForm.value);
    handleForgotClick();
  }
}

async function handleLoginClick(type) {
  if (type === "google") {
    userStore.googleLogin();
  } else {
    const isValid = await v$.value.$validate();
    if (isValid) {
      await userStore.login(form.value);
    }
  }
}
</script>

<template>
  <div
    v-if="!resetView"
    class="d-flex flex-column justify-center align-center container-background h-100"
  >
    <div class="mb-5" style="width: 15%">
      <Logo />
    </div>
    <v-card class="pa-5" min-width="400px">
      <v-form>
        <div class="d-flex flex-column gap-10">
          <v-text-field
            id="id"
            v-model="form.email"
            name="email"
            type="email"
            variant="outlined"
            :error-messages="getErrorMessages('email').value"
            hide-details="auto"
            @blur="v$.email.$touch()"
          >
            <template #label> Email </template>
          </v-text-field>
          <v-text-field
            id="id"
            v-model="form.password"
            name="password"
            :type="showPass ? 'text' : 'password'"
            label="Password"
            variant="outlined"
            :append-inner-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
            :error-messages="getErrorMessages('password').value"
            hide-details="auto"
            @click:append-inner="showPass = !showPass"
            @blur="v$.password.$touch()"
          />
          <div class="mb-5 d-flex justify-space-between align-center">
            <v-btn color="primary" @click="handleLoginClick('local')">
              Login
            </v-btn>
            <div>
              <v-btn
                variant="text"
                color="primary"
                size="small"
                @click="handleForgotClick"
              >
                Forgot your password?
              </v-btn>
            </div>
          </div>
        </div>
        <v-divider />
        <div class="mt-5 d-flex justify-center align-center">
          <v-btn color="primary" @click="handleLoginClick('google')">
            <v-icon icon="mdi-google" class="mr-2" /> Login with Google
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
  <div
    v-else
    class="d-flex flex-column justify-center align-center container-background h-100"
  >
    <v-card class="pa-5" min-width="400px">
      <v-form>
        <div class="d-flex flex-column gap-10">
          <v-text-field
            id="id"
            v-model="resetForm.email"
            name="email"
            type="email"
            variant="outlined"
            :error-messages="getResetErrorMessages('email').value"
            hide-details="auto"
            @blur="v2$.email.$touch()"
          >
            <template #label> Email </template>
          </v-text-field>
          <div class="mb-5 d-flex justify-space-between align-center">
            <v-btn color="primary" @click="handleRequestClick">
              Request new password
            </v-btn>
            <div>
              <v-btn
                variant="text"
                color="primary"
                size="small"
                @click="handleForgotClick"
              >
                back to login
              </v-btn>
            </div>
          </div>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<style lang="scss" scoped>
.container-background {
  background: rgb(var(--v-theme-primary));
  .v-form {
    a {
      color: rgb(var(--v-theme-primary));
    }
  }
}
</style>
